import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Plus, Loader2, PackageOpen } from "lucide-react";
import { toast } from "sonner";
import ProductForm from "./ProductForm";
import ProductRow from "./ProductRow";
import { errorText } from "./imageUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DRAFT_KEY = "timerstore_product_draft";
const BASE_CATEGORIES = ["Cosplay", "Figuren", "Dekoration", "Zubehör"];

const EMPTY = {
  name: "", variant: "", price: "", stock: "1", badge: "",
  category: "Figuren", active: true, description: "", images: [],
};

// Entwurf (nur öffentliche Produktdaten) übersteht ein Neuladen der Seite
const loadDraft = () => {
  try {
    return JSON.parse(sessionStorage.getItem(DRAFT_KEY)) || null;
  } catch {
    return null;
  }
};

const toForm = (p) => ({
  name: p.name,
  variant: p.variant || "",
  price: String(p.price).replace(".", ","),
  stock: String(p.stock ?? 0),
  badge: p.badge || "",
  category: p.category || "Figuren",
  active: p.active ?? true,
  description: p.description || "",
  images: p.images?.length ? p.images : [p.image].filter(Boolean),
});

const toPayload = (form, price, stock) => ({
  name: form.name.trim(),
  variant: form.variant.trim(),
  price: Math.round(price * 100) / 100,
  stock,
  badge: form.badge,
  category: form.category.trim() || "Diverses",
  in_stock: form.active,
  description: form.description.trim(),
  images: form.images,
});

const ProductManager = () => {
  const [draft] = useState(loadDraft);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(draft?.editing || null);
  const [form, setForm] = useState(draft?.form || EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ editing, form }));
    else sessionStorage.removeItem(DRAFT_KEY);
  }, [editing, form]);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/products`, { withCredentials: true });
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch (err) {
      toast.error(errorText(err, "Produkte konnten nicht geladen werden."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const categories = [...new Set([...items.map((p) => p.category).filter(Boolean), ...BASE_CATEGORIES])];

  const open = (id, data) => {
    setForm(data);
    setEditing(id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async (e) => {
    e.preventDefault();
    const price = parseFloat(String(form.price).replace(",", "."));
    const stock = parseInt(form.stock, 10);
    if (!(price > 0)) return toast.error("Bitte einen gültigen Preis eingeben.");
    if (!(stock >= 0)) return toast.error("Bitte eine gültige Stückzahl eingeben (0 oder mehr).");
    const isNew = editing === "new";
    setSaving(true);
    try {
      const payload = toPayload(form, price, stock);
      if (isNew) await axios.post(`${API}/admin/products`, payload, { withCredentials: true });
      else await axios.put(`${API}/admin/products/${editing}`, payload, { withCredentials: true });
      toast.success(isNew ? "Produkt angelegt." : "Produkt aktualisiert.");
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(errorText(err, "Speichern fehlgeschlagen."));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`„${p.name}${p.variant ? ` – ${p.variant}` : ""}“ wirklich löschen?`)) return;
    try {
      await axios.delete(`${API}/admin/products/${p.id}`, { withCredentials: true });
      toast.success("Produkt gelöscht.");
      if (editing === p.id) setEditing(null);
      await load();
    } catch (err) {
      toast.error(errorText(err, "Löschen fehlgeschlagen."));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12" data-testid="products-loading">
        <Loader2 className="h-8 w-8 text-[#f7941e] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="product-manager">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-slate-500 text-sm" data-testid="products-count">
          {items.length} Produkt{items.length === 1 ? "" : "e"} im Shop
        </p>
        <button onClick={() => open("new", EMPTY)} data-testid="product-add-button"
          className="inline-flex items-center gap-2 rounded-full px-5 h-11 font-bold text-white bg-gradient-to-r from-[#f7941e] to-[#e8760a] shadow-[0_8px_20px_rgba(247,148,30,0.35)] hover:-translate-y-0.5 transition-transform">
          <Plus className="h-5 w-5" /> Neues Produkt
        </button>
      </div>

      {editing && (
        <ProductForm isNew={editing === "new"} form={form} setForm={setForm} categories={categories}
          saving={saving} onSubmit={save} onCancel={() => setEditing(null)} />
      )}

      {items.length === 0 && (
        <div className="text-center py-12" data-testid="products-empty">
          <PackageOpen className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Noch keine Produkte im Shop. Lege dein erstes Produkt an.</p>
        </div>
      )}
      {items.length > 0 && (
        <div className="space-y-3" data-testid="products-list">
          {items.map((p) => (
            <ProductRow key={p.id} p={p} onEdit={(x) => open(x.id, toForm(x))} onDelete={remove} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductManager;
